import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { Flexbox, InputField, WrapperBox } from "../../Theme/GlobalStyles";
import { Text } from "../../Theme/Typography";
import { globalTheme } from "../../Theme/Theme";
import { AngleDown, AngleLeft, AngleRight, InfoIcon, Search } from "../../Theme/Icons";
import { PopupBody, PopupWrapper } from "../../Components/Popup/CommonPopup";
import { GUIDELINES, TUTORIALS, searchHelp } from "../../Constants/eduspaceHelp";

const TABS = ["Tutorials", "Guidelines"];

/* Same segmented control the Eduspace dashboard uses for its day/week toggle. */
const SegmentedToggle = ({ options, value, onChange }) => (
    <WrapperBox
        padding="6px 10px"
        background={globalTheme.colors.nuetral2}
        borderRadius={20}
        maxWidth="max-content"
        overflow="visible"
    >
        <Flexbox alignCenter maxWidth="max-content" gap={4}>
            {options.map((opt) => {
                const active = opt === value;
                return (
                    <Flexbox
                        key={opt}
                        alignCenter
                        justiCenter
                        maxWidth="max-content"
                        onClick={() => onChange?.(opt)}
                        padding="6px 18px"
                        borderRadius={40}
                        className="pointer"
                        background={active ? globalTheme.colors.white1 : "transparent"}
                    >
                        <Text font12 inter medium {...(active ? { black3: true } : { nuetral13: true })}>
                            {opt}
                        </Text>
                    </Flexbox>
                );
            })}
        </Flexbox>
    </WrapperBox>
);

/* ------------------------------------------------------------------ *
 * Tutorial card
 * ------------------------------------------------------------------ */

const TutorialCard = styled.div`
    border: 1px solid ${globalTheme.colors.nuetral2};
    border-radius: 20px;
    background: ${globalTheme.colors.white1};
    overflow: hidden;
    line-height: normal;
    /* Width + max-width (rather than flex-basis) so the .respCard3 breakpoints in
       GlobalStyles can take over with their !important max-widths: 3-up -> 2-up -> 1-up. */
    width: 100%;
    max-width: calc(33.333% - 11px);
    flex-grow: 0;
    transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
    cursor: ${({ $ready }) => ($ready ? "pointer" : "default")};
    opacity: ${({ $ready }) => ($ready ? 1 : 0.65)};

    &:hover {
        ${({ $ready }) =>
        $ready &&
        `transform: translateY(-3px);
             box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
             border-color: ${globalTheme.colors.grey6};`}
    }

    &:hover .playCircle {
        ${({ $ready }) => $ready && `transform: scale(1.08);`}
    }

    &:focus-visible {
        outline: 2px solid ${globalTheme.colors.primary};
        outline-offset: 2px;
    }
`;

/* 16:9 thumbnail. No poster images exist yet, so the default face is the dark app
   chrome washed with the Eduspace brand gradient. */
const Thumb = styled.div`
    position: relative;
    width: 100%;
    padding-top: 56.25%;
    background: ${globalTheme.colors.black4};
    overflow: hidden;

    /* Brand wash only on the placeholder face -- a real poster shouldn't be tinted. */
    &::after {
        content: "";
        position: absolute;
        inset: 0;
        background: ${globalTheme.colors.gradient};
        opacity: ${({ $hasPoster }) => ($hasPoster ? 0 : 0.18)};
    }

    img {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .thumbLayer {
        position: absolute;
        inset: 0;
        z-index: 2;
    }

    .playCircle {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.94);
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
        transition: transform 0.18s ease;
        z-index: 3;
    }

    .chip {
        position: absolute;
        z-index: 3;
        padding: 4px 10px;
        border-radius: 20px;
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        line-height: normal;
    }

    .chipTL {
        top: 12px;
        left: 12px;
    }

    .chipBR {
        bottom: 12px;
        right: 12px;
    }
`;

const PlayGlyph = () => (
    <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M16.4 8.27L3.3 0.7C2.03 -0.03 0.5 0.89 0.5 2.35V17.5C0.5 18.96 2.03 19.88 3.3 19.15L16.4 11.58C17.67 10.85 17.67 9 16.4 8.27Z"
            fill={globalTheme.colors.black4}
        />
    </svg>
);

/* ------------------------------------------------------------------ *
 * Guideline accordion
 * ------------------------------------------------------------------ */

const AccordionRow = styled.div`
    border: 1px solid ${({ $open }) => ($open ? globalTheme.colors.primary3 : globalTheme.colors.nuetral2)};
    border-radius: 16px;
    background: ${globalTheme.colors.white1};
    overflow: hidden;
    line-height: normal;
    margin-bottom: 12px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    ${({ $open }) => $open && `box-shadow: 0 4px 18px rgba(255, 113, 67, 0.08);`}

    .accHead {
        cursor: pointer;
        user-select: none;
    }

    .chevron {
        transition: transform 0.2s ease;
        transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
    }

    .accBody {
        max-height: ${({ $open }) => ($open ? "600px" : "0px")};
        opacity: ${({ $open }) => ($open ? 1 : 0)};
        overflow: hidden;
        transition: max-height 0.28s ease, opacity 0.2s ease, padding 0.28s ease;
        padding: ${({ $open }) => ($open ? "0 20px 20px 66px" : "0 20px 0 66px")};
    }
`;

const StepChip = styled.div`
    width: 34px;
    height: 34px;
    min-width: 34px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ $open }) => ($open ? globalTheme.colors.primary : globalTheme.colors.primary1)};
    transition: background 0.2s ease;
`;

const VideoFrame = styled.video`
    width: 100%;
    max-height: 62vh;
    border-radius: 20px;
    background: #000;
    display: block;
`;

const NavCircle = styled.button`
    width: 34px;
    height: 34px;
    min-width: 34px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.28);
    background: rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.22);
    }
`;

/* Sits over the top-right corner of the video. PopupBody's own `.closeIcon` slot is
   painted over by the <video>, so this needs its own stacking context. */
const CloseVideoBtn = styled.button`
    position: absolute;
    top: 26px;
    right: 26px;
    z-index: 5;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.35);
    background: rgba(0, 0, 0, 0.55);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s ease, transform 0.15s ease;

    &:hover {
        background: rgba(0, 0, 0, 0.8);
        transform: scale(1.06);
    }
`;

const pad2 = (n) => String(n).padStart(2, "0");

const EduspaceGuidelines = () => {
    const [tab, setTab] = useState(TABS[0]);
    const [search, setSearch] = useState("");
    const [openSection, setOpenSection] = useState(GUIDELINES[0].id);
    const [activeVideo, setActiveVideo] = useState(null);

    const query = search.trim();
    const results = useMemo(() => searchHelp(query), [query]);
    const { tutorials, guidelines } = results;

    /* A search should surface the matching policy text, not make the user open six rows. */
    useEffect(() => {
        if (query && guidelines.length) setOpenSection(guidelines[0].id);
    }, [query, guidelines]);

    const playableTutorials = useMemo(() => TUTORIALS.filter((t) => t.videoUrl), []);

    const stepVideo = (delta) => {
        if (!activeVideo || playableTutorials.length < 2) return;
        const idx = playableTutorials.findIndex((t) => t.id === activeVideo.id);
        const next = (idx + delta + playableTutorials.length) % playableTutorials.length;
        setActiveVideo(playableTutorials[next]);
    };

    useEffect(() => {
        if (!activeVideo) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") setActiveVideo(null);
            if (e.key === "ArrowRight") stepVideo(1);
            if (e.key === "ArrowLeft") stepVideo(-1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeVideo]);

    const openTutorial = (tutorial) => {
        if (!tutorial.videoUrl) return;
        setActiveVideo(tutorial);
    };

    const renderTutorials = () => {
        if (!tutorials.length) {
            return (
                <Flexbox column alignCenter justiCenter gap={8} padding="60px 20px">
                    <Text font16 inter semiBold black3>No tutorials match “{query}”</Text>
                    <Text font13 inter medium nuetral6>Try a shorter word, or switch to the Guidelines tab.</Text>
                </Flexbox>
            );
        }

        return (
            <Flexbox wrap alignStart gap={16}>
                {tutorials.map((tutorial) => {
                    const ready = Boolean(tutorial.videoUrl);
                    return (
                        <TutorialCard
                            key={tutorial.id}
                            className="respCard3"
                            $ready={ready}
                            tabIndex={ready ? 0 : -1}
                            role={ready ? "button" : undefined}
                            onClick={() => openTutorial(tutorial)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    openTutorial(tutorial);
                                }
                            }}
                        >
                            <Thumb $hasPoster={Boolean(tutorial.poster)}>
                                {tutorial.poster && <img src={tutorial.poster} alt="" />}
                                <div className="thumbLayer">
                                    <div className="chip chipTL">
                                        <Text font11 inter semiBold white>{pad2(tutorial.step)}</Text>
                                    </div>
                                    {ready ? (
                                        <>
                                            <div className="playCircle"><PlayGlyph /></div>
                                            {tutorial.duration && (
                                                <div className="chip chipBR">
                                                    <Text font11 inter medium white>{tutorial.duration}</Text>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="chip chipBR">
                                            <Text font11 inter medium white>Coming soon</Text>
                                        </div>
                                    )}
                                </div>
                            </Thumb>
                            <Flexbox column gap={6} padding="16px">
                                <Text font16 inter semiBold black3 lineClamp={1} maxWidth="100%">
                                    {tutorial.title}
                                </Text>
                                <Text font13 inter medium nuetral6 lineClamp={2} maxWidth="100%">
                                    {tutorial.blurb}
                                </Text>
                            </Flexbox>
                        </TutorialCard>
                    );
                })}
            </Flexbox>
        );
    };

    const renderGuidelines = () => {
        if (!guidelines.length) {
            return (
                <Flexbox column alignCenter justiCenter gap={8} padding="60px 20px">
                    <Text font16 inter semiBold black3>No guidelines match “{query}”</Text>
                    <Text font13 inter medium nuetral6>Try a shorter word, or switch to the Tutorials tab.</Text>
                </Flexbox>
            );
        }

        return (
            <>
                {guidelines.map((section) => {
                    const open = openSection === section.id;
                    return (
                        <AccordionRow key={section.id} $open={open}>
                            <Flexbox
                                spaceBW
                                alignCenter
                                gap={14}
                                padding="16px 20px"
                                className="accHead"
                                onClick={() => setOpenSection(open ? null : section.id)}
                            >
                                <Flexbox alignCenter gap={12} maxWidth="max-content">
                                    <StepChip $open={open}>
                                        <Text font13 inter semiBold {...(open ? { white: true } : { primary: true })}>
                                            {section.step}
                                        </Text>
                                    </StepChip>
                                    <Text font16 inter semiBold black3>{section.title}</Text>
                                </Flexbox>
                                <AngleDown className="chevron" />
                            </Flexbox>

                            <div className="accBody">
                                <Flexbox column gap={12}>
                                    {(section.paras || []).map((para, i) => (
                                        <Text key={i} font14 inter regular nuetral13 lineHeight={165}>
                                            {para}
                                        </Text>
                                    ))}

                                    {section.list && (
                                        <Flexbox column gap={8}>
                                            {section.list.map((item, i) => (
                                                <Flexbox
                                                    key={item}
                                                    alignCenter
                                                    gap={12}
                                                    padding="10px 14px"
                                                    borderRadius={10}
                                                    background={globalTheme.colors.grey8}
                                                >
                                                    <Text font12 inter semiBold primary>{pad2(i + 1)}</Text>
                                                    <Text font14 inter medium black3>{item}</Text>
                                                </Flexbox>
                                            ))}
                                        </Flexbox>
                                    )}
                                </Flexbox>
                            </div>
                        </AccordionRow>
                    );
                })}

                <WrapperBox
                    background={globalTheme.colors.yellow2}
                    borderColor={globalTheme.colors.yellow}
                    borderRadius={16}
                    padding="14px 18px"
                    overflow="visible"
                >
                    <Flexbox alignCenter gap={12}>
                        <InfoIcon />
                        <Text font13 inter medium black3>
                            Need an exception, or something outside the booking window? Reach out to the
                            Operations Team before submitting your request.
                        </Text>
                    </Flexbox>
                </WrapperBox>
            </>
        );
    };

    const activeCount = tab === TABS[0] ? tutorials.length : guidelines.length;

    return (
        <>
            <WrapperBox
                padding={"12px 20px"}
                background={globalTheme.colors.white1}
                boxShadow
                overflow={"visible"}
            >
                <Flexbox spaceBW alignCenter gap={16}>
                    <Flexbox column justiCenter maxWidth="max-content">
                        <Text font16 inter semiBold black3>Guidelines</Text>
                        <Text font14 inter regular grey2>
                            Everything you need to book a classroom on Eduspace
                        </Text>
                    </Flexbox>
                    <Flexbox maxWidth={"40%"} flexEnd gap={18}>
                        <InputField
                            noLabel
                            hasIcon={<Search />}
                            revIcon
                            maxWidth="300px"
                            placeholder="Search guidelines and tutorials ..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </Flexbox>
                </Flexbox>
            </WrapperBox>

            <WrapperBox background={"transparent"} maxHeight={"calc(100% - 75px)"}>
                <Flexbox spaceBW alignCenter gap={16} className="mb20">
                    <Flexbox column gap={4} maxWidth="max-content">
                        <Text font24 inter semiBold black3>Guidelines &amp; Tutorials</Text>
                        <Text font13 inter medium nuetral6>
                            {query
                                ? `${activeCount} ${activeCount === 1 ? "result" : "results"} for “${query}”`
                                : `${TUTORIALS.length} walkthrough videos · ${GUIDELINES.length} policy sections`}
                        </Text>
                    </Flexbox>
                    <SegmentedToggle options={TABS} value={tab} onChange={setTab} />
                </Flexbox>

                {tab === TABS[0] ? renderTutorials() : renderGuidelines()}
            </WrapperBox>

            {/* PopupWrapper stays mounted and is toggled by `.active`; the <video> itself is
                rendered only while open so nothing is fetched until a card is clicked. */}
            <PopupWrapper
                className={activeVideo ? "active" : ""}
                onClick={() => setActiveVideo(null)}
            >
                {/* Stop backdrop-close from firing on clicks inside the player. */}
                <PopupBody maxWidth={940} padding={16} onClick={(e) => e.stopPropagation()}>
                    {activeVideo && (
                        <Flexbox column gap={16}>
                            <CloseVideoBtn
                                className="pointer"
                                onClick={() => setActiveVideo(null)}
                                aria-label="Close video"
                            >
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1 1L13 13M13 1L1 13" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
                                </svg>
                            </CloseVideoBtn>
                            <VideoFrame
                                key={activeVideo.id}
                                src={activeVideo.videoUrl}
                                controls
                                autoPlay
                                playsInline
                                preload="metadata"
                            />
                            <Flexbox spaceBW alignCenter gap={16}>
                                <Flexbox column gap={4}>
                                    <Text font16 inter semiBold white>
                                        {pad2(activeVideo.step)} · {activeVideo.title}
                                    </Text>
                                    <Text font13 inter medium grey3>{activeVideo.blurb}</Text>
                                </Flexbox>
                                {playableTutorials.length > 1 && (
                                    <Flexbox alignCenter gap={10} maxWidth="max-content">
                                        <NavCircle className="pointer" onClick={() => stepVideo(-1)} aria-label="Previous tutorial">
                                            <AngleLeft color={globalTheme.colors.white1} />
                                        </NavCircle>
                                        <NavCircle className="pointer" onClick={() => stepVideo(1)} aria-label="Next tutorial">
                                            <AngleRight color={globalTheme.colors.white1} />
                                        </NavCircle>
                                    </Flexbox>
                                )}
                            </Flexbox>
                        </Flexbox>
                    )}
                </PopupBody>
            </PopupWrapper>
        </>
    );
};

export default EduspaceGuidelines;
